/**
 *  @swagger
 *  definitions:
 *    RegionSeasonParams:
 *      type: object
 *      properties:
 *        RegionId:
 *          description: ID of the associated Region
 *          type: string
 *        Name:
 *          description: Name of the RegionSeason
 *          type: string
 *        StartDate:
 *          description: Start Date of the RegionSeason
 *          type: string
 *          format: date-time
 *        EndDate:
 *          description: End Date of the RegionSeason
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegionSeasonParams'
 *        - type: object
 *          required:
 *            - RegionId
 *            - Name
 *            - StartDate
 *            - EndDate
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegionSeason
 *              type: string
 *        - $ref: '#/definitions/RegionSeasonParams'
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegionSeasonQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegionSeason:
 *      allOf:
 *        - $ref: '#/definitions/RegionSeasonCreateParams'
 *        - $ref: '#/definitions/RegionSeasonUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegionSeason:
 *        allOf:
 *          - $ref: '#/definitions/RegionSeason'
 *          - type: object
 *            properties:
 *              Region:
 *                description: Region of the RegionSeason
 *                $ref: '#/components/schemas/Region'
 *              Regattas:
 *                description: Regattas of the RegionSeason
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Regatta'
 *              Championships:
 *                description: Championships of the RegionSeason
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Championship'
 *              Advertisements:
 *                description: Advertisements of the RegionSeason
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Advertisement'
 *              RegionSeasonRegattaLocations:
 *                description: RegionSeasonRegattaLocations of the RegionSeason
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegionSeasonRegattaLocation'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegionId: string;
};

export type $Params = {
  EndDate: Date;
  Name: string;
  StartDate: Date;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Advertisements?: Array<$Model<'Advertisement'>>;
  Championships?: Array<$Model<'Championship'>>;
  Regattas?: Array<$Model<'Regatta'>>;
  Region?: $Model<'Region'>;
  RegionSeasonRegattaLocations?: Array<$Model<'RegionSeasonRegattaLocation'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  EndDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  StartDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegionSeason'> => {
  const RegionSeason = stack.define(
    'RegionSeason',
    fields(Sequelize),
    {
      tableName: 'RegionSeasons',
    },
  );

  RegionSeason.associate = (models: $ModelClasses) => {
    models.RegionSeason.belongsTo(
      models.Region,
      {
        as: 'Region',
        foreignKey: 'RegionId',
      },
    );
    models.RegionSeason.hasMany(
      models.Regatta,
      {
        as: 'Regattas',
        foreignKey: 'RegionSeasonId',
      },
    );
    models.RegionSeason.hasMany(
      models.Championship,
      {
        as: 'Championships',
        foreignKey: 'RegionSeasonId',
      },
    );
    models.Region.hasMany(
      models.Advertisement,
      {
        as: 'Advertisements',
        foreignKey: 'RegionSeasonId',
      },
    );
    models.Region.hasMany(
      models.RegionSeasonRegattaLocation,
      {
        as: 'RegionSeasonRegattaLocations',
        foreignKey: 'RegionSeasonId',
      },
    );
  };

  RegionSeason.addScope(
    'defaultScope',
    {
      order: [['StartDate', 'DESC']],
    },
  );

  return RegionSeason;
});
